import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { UniverseComponent } from 'src/domains/common/universes';
import { useCommon } from 'src/domains/common/Common';
import { useAsyncButtonAction } from 'src/domains/common/hooks/useAsyncButtonAction';
import { AppPlayersState } from 'src/domains/players/state/AppPlayers.state';
import { Popup } from 'src/domains/players/webview/ui/Popup';
import { Loader } from 'src/domains/layouts/webview/components/loader/Loader';
import {
    AcceptBtn,
    Content,
    FaqModalScroll,
    OverlayDefault,
    PopupDescription,
    PopupHeader,
    ReviewSaferGamblingBtn,
} from 'src/domains/players/webview/components/Account/Notifications/BlockingNotifications.style';

interface BlockingDepositLimitCalendarNotificationPropsType {
    onConfirm: () => Promise<void>;
}

export const BlockingDepositLimitCalendarNotification = observer(
    'BlockingDepositLimitCalendarNotification',
    ({ onConfirm }: BlockingDepositLimitCalendarNotificationPropsType): JSX.Element => {
        const common = useCommon();
        const { cmsDrawerPagesState } = AppPlayersState.get(common);
        const [isFaqOpen, setIsFaqOpen] = useState(false);
        const { handleClick: handleAcknowledge, isLoading: isAcknowledgeLoading } = useAsyncButtonAction(onConfirm);
        const { displayPage, pageType, title, content } = cmsDrawerPagesState;
        const isFaqVisible = isFaqOpen && displayPage === true && pageType === 'help';

        const openFaq = (): void => {
            setIsFaqOpen(true);
            cmsDrawerPagesState.showHelpPage();
        };

        const closeFaq = (): void => {
            setIsFaqOpen(false);
            cmsDrawerPagesState.closePage();
        };

        return (
            <OverlayDefault data-test='overlay-prompt'>
                <Content>
                    <UniverseComponent>
                        {(universeModule): React.ReactElement => <universeModule.compliancePopupLogo />}
                    </UniverseComponent>
                    <PopupHeader>
                        <I18n
                            langKey='account.blocking-popup.deposit-limit-calendar.title'
                            defaultText="We've updated how your deposit limit works"
                        />
                    </PopupHeader>
                    <PopupDescription data-test='account.blocking-popup.deposit-limit-calendar.description'>
                        <I18n
                            langKey='account.blocking-popup.deposit-limit-calendar.description.changed'
                            defaultText="We've changed the way the time period for your deposit limit is calculated. Your deposit limit amount has not changed."
                        />
                    </PopupDescription>
                    <PopupDescription>
                        <I18n
                            langKey='account.blocking-popup.deposit-limit-calendar.description.calendar-basis'
                            defaultText='Your daily, weekly, and monthly limits will now apply on a calendar basis rather than over a rolling period.'
                        />
                    </PopupDescription>
                    <PopupDescription>
                        <I18n
                            langKey='account.blocking-popup.deposit-limit-calendar.description.resets'
                            defaultText='This means your daily limit resets each calendar day, your weekly limit resets each calendar week, and your monthly limit resets each calendar month.'
                        />
                    </PopupDescription>
                    <PopupDescription>
                        <I18n
                            langKey='account.blocking-popup.deposit-limit-calendar.description.amounts'
                            defaultText='Your deposit limit amounts remain the same.'
                        />
                    </PopupDescription>
                    <PopupDescription>
                        <I18n
                            langKey='account.blocking-popup.deposit-limit-calendar.description.faq'
                            defaultText='Please see our FAQ for more information'
                        />
                    </PopupDescription>
                    <AcceptBtn
                        onClick={handleAcknowledge}
                        size='medium'
                        isLoading={isAcknowledgeLoading}
                        disabled={isAcknowledgeLoading}
                        dataTest='deposit-limit-calendar-acknowledge'
                    >
                        <I18n
                            langKey='account.blocking-popup.deposit-limit-calendar.acknowledge'
                            defaultText='Acknowledge'
                        />
                    </AcceptBtn>
                    <ReviewSaferGamblingBtn
                        onClick={openFaq}
                        size='medium'
                        disabled={isAcknowledgeLoading}
                        dataTest='deposit-limit-calendar-view-faq'
                    >
                        <I18n
                            langKey='account.blocking-popup.deposit-limit-calendar.view-faq'
                            defaultText='View FAQ'
                        />
                    </ReviewSaferGamblingBtn>
                </Content>
                {isFaqVisible ? (
                    <Popup
                        title={<span dangerouslySetInnerHTML={{ __html: title ?? 'FAQ' }} />}
                        onClose={closeFaq}
                        maxWidth='500px'
                        minWidth='500px'
                        dataTest='deposit-limit-calendar-faq-modal'
                    >
                        {content === null ? (
                            <Loader version='light' />
                        ) : (
                            <FaqModalScroll dangerouslySetInnerHTML={{ __html: content }} />
                        )}
                    </Popup>
                ) : null}
            </OverlayDefault>
        );
    }
);
